import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBarraSep, PlnBloco, stickyTh } from './PanelShell'
import { brl, ptDate, paraNumero } from './finance-shared'
import { FiLoader, FiAlertTriangle, FiCheck, FiSave } from 'react-icons/fi'
import { LuWallet } from 'react-icons/lu'

// Conferência de caixa. Conta de dinheiro não tem extrato bancário, então nada
// no sistema conferia o saldo dela: a Caixinha passou 8 meses no vermelho sem
// ninguém ver (auditoria 03/08/2026). Aqui a contagem física do cofre é o
// valor externo — o LEDGERBAL do caixa — e a diferença aparece na hora.

interface Janela { inicio: string; fim: string | null; pior: number; pior_data: string; pior_descricao: string | null; n: number }
interface Contagem {
  id?: number; count_date: string; counted_amount: string | number; system_amount: string | number
  diff: string | number; note: string | null; created_by: string | null; created_at?: string
}
interface Caixa {
  id: number; name: string; initial_balance: number; saldo_sistema: number
  ultima_contagem: Contagem | null; janelas_negativas: Janela[]
}

const hojeISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const inputCls = 'neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

export function ConferenciaCaixa() {
  const { authenticatedFetch } = useAuth()
  const [caixas, setCaixas] = useState<Caixa[]>([])
  const [contaId, setContaId] = useState<number | null>(null)
  const [data, setData] = useState(hojeISO)
  const [contado, setContado] = useState('')
  const [nota, setNota] = useState('')
  const [saldoSistema, setSaldoSistema] = useState<number | null>(null)
  const [jaExiste, setJaExiste] = useState<{ counted_amount: string; note: string | null } | null>(null)
  const [historico, setHistorico] = useState<Contagem[]>([])
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const caixa = caixas.find(c => c.id === contaId) || null

  const carregarCaixas = useCallback(async () => {
    setLoading(true); setErro(null)
    try {
      const r = await authenticatedFetch('/api/cash-count/accounts')
      const d = await r.json()
      if (!d.success) { setErro(d.error || 'Falha ao carregar'); return }
      setCaixas(d.contas)
      setContaId(prev => prev ?? (d.contas[0]?.id ?? null))
    } catch { setErro('Erro de conexão') } finally { setLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => { carregarCaixas() }, [carregarCaixas])

  // O saldo do sistema é sempre o DAQUELA data — lançar retroativo muda o que a
  // conferência de ontem deveria ter visto, então nunca reaproveitamos o saldo
  // de hoje pra uma data anterior.
  const carregarPreview = useCallback(async () => {
    if (!contaId || !data) return
    try {
      const qs = new URLSearchParams({ bank_account_id: String(contaId), count_date: data })
      const r = await authenticatedFetch(`/api/cash-count/preview?${qs}`)
      const d = await r.json()
      if (!d.success) { setErro(d.error || 'Falha ao calcular o saldo'); return }
      setSaldoSistema(d.saldo_sistema)
      setJaExiste(d.existente ? { counted_amount: String(d.existente.counted_amount), note: d.existente.note } : null)
    } catch { setErro('Erro de conexão') }
  }, [authenticatedFetch, contaId, data])

  const carregarHistorico = useCallback(async () => {
    if (!contaId) return
    try {
      const r = await authenticatedFetch(`/api/cash-count?bank_account_id=${contaId}`)
      const d = await r.json()
      if (d.success) setHistorico(d.conferencias)
    } catch { /* histórico é secundário: não bloqueia a conferência */ }
  }, [authenticatedFetch, contaId])

  useEffect(() => { carregarPreview(); carregarHistorico() }, [carregarPreview, carregarHistorico])

  // Trocar de conta ou de data zera o que foi digitado: o número pertence a um
  // caixa e a um dia, e carregar por engano a contagem errada é pior que redigitar.
  useEffect(() => { setContado(''); setNota(''); setOk(null); setErro(null) }, [contaId, data])

  const valorContado = contado.trim() ? paraNumero(contado) : null
  const diff = valorContado != null && saldoSistema != null ? Math.round((valorContado - saldoSistema) * 100) / 100 : null
  const bate = diff != null && Math.abs(diff) < 0.005

  const salvar = async () => {
    if (!contaId || valorContado == null) return
    setSalvando(true); setErro(null); setOk(null)
    try {
      const r = await authenticatedFetch('/api/cash-count', {
        method: 'POST',
        body: JSON.stringify({ bank_account_id: contaId, count_date: data, counted_amount: valorContado, note: nota || null }),
      })
      const d = await r.json()
      if (!d.success) { setErro(d.error || 'Falha ao gravar'); return }
      setOk(Math.abs(d.diff) < 0.005
        ? 'Conferência gravada — caixa fechado, sem diferença.'
        : `Conferência gravada com diferença de ${brl(d.diff)}.`)
      setContado(''); setNota('')
      await Promise.all([carregarCaixas(), carregarPreview(), carregarHistorico()])
    } catch { setErro('Erro de conexão') } finally { setSalvando(false) }
  }

  // PLANOS (21/10/2026): todas as saídas da tela levam o mesmo cabeçalho.
  const cab = {
    titulo: 'Conferência de caixa',
    sub: 'Bata a contagem do cofre com o saldo do sistema',
    icone: <LuWallet />,
  }

  if (loading) {
    return <PanelShell {...cab}><div className="flex-1 flex items-center justify-center">
      <FiLoader className="h-5 w-5 animate-spin text-muted-foreground" />
    </div></PanelShell>
  }

  if (!caixas.length) {
    return <PanelShell {...cab}><div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">
          <LuWallet className="h-8 w-8 text-primary/80" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Nenhum caixa cadastrado</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A conferência vale para contas do tipo <b>dinheiro</b>. Cadastre uma em
          Cadastros → Contas e Cobrança.
        </p>
      </div>
    </div></PanelShell>
  }

  const janelas = caixa?.janelas_negativas || []
  const janelaAberta = janelas.length > 0 && !janelas[janelas.length - 1].fim

  return (
    <PanelShell {...cab} toolbar={
      /* PLANOS: seletores numa PlnBarra, rótulo ao lado do campo */
      <PlnBarra>
        <PlnBarraRot>Caixa</PlnBarraRot>
        <div className="min-w-[12rem]">
          <select aria-label="Caixa" className={inputCls} value={contaId ?? ''} onChange={e => setContaId(Number(e.target.value))}>
            {caixas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Data da contagem</PlnBarraRot>
        <div>
          <input type="date" aria-label="Data da contagem" className={inputCls} value={data} max={hojeISO()} onChange={e => setData(e.target.value)} />
        </div>
      </PlnBarra>
    }>
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pb-2">

        {/* ── Alerta de saldo negativo ──
            Vem antes de tudo: um caixa pode fechar hoje e ainda ter passado
            meses no vermelho, e é o vermelho que denuncia entrada não lançada. */}
        {janelas.length > 0 && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <FiAlertTriangle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground">
                  Saldo negativo em {janelas.length === 1 ? 'uma janela' : `${janelas.length} janelas`}
                  {janelaAberta && ' — e o caixa está negativo agora'}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Caixa físico não fica negativo. Saiu dinheiro que nunca entrou no sistema:
                  falta lançar entrada.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {janelas.map((j, i) => (
                    <div key={i} className="rounded-lg border border-border bg-card px-3 py-2">
                      <div className="text-xs text-muted-foreground tabular-nums">
                        {ptDate(j.inicio)} → {j.fim ? ptDate(j.fim) : 'hoje'}
                      </div>
                      <div className="text-base font-semibold text-destructive tabular-nums">{brl(j.pior)}</div>
                      <div className="text-xs text-muted-foreground truncate" title={j.pior_descricao || ''}>
                        pior em {ptDate(j.pior_data)} · {j.n} {j.n === 1 ? 'linha' : 'linhas'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── A conferência ── (PLANOS: virou PlnBloco, título no cabeçalho do bloco) */}
        <PlnBloco titulo={`Conferência de ${ptDate(data)}`}>
        <div className="p-4 sm:p-5">

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Saldo no sistema</div>
              <div className="text-2xl font-semibold text-foreground tabular-nums">
                {saldoSistema == null ? '—' : brl(saldoSistema)}
              </div>
            </div>

            <div>
              <label htmlFor="contado" className="block text-xs font-medium text-muted-foreground mb-1">
                Quanto tem no cofre
              </label>
              <input
                id="contado" className={inputCls} inputMode="decimal" placeholder="0,00"
                value={contado} onChange={e => setContado(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && valorContado != null) salvar() }}
              />
              {jaExiste && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Já conferido neste dia: {brl(jaExiste.counted_amount)} — gravar de novo corrige.
                </p>
              )}
            </div>

            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Diferença</div>
              <div className={`text-2xl font-semibold tabular-nums ${
                diff == null ? 'text-muted-foreground' : bate ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'
              }`}>
                {diff == null ? '—' : bate ? brl(0) : brl(diff)}
              </div>
              {diff != null && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {bate ? 'Caixa fecha.' : diff > 0 ? 'Sobra no cofre — falta lançar entrada.' : 'Falta no cofre — falta lançar saída.'}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[14rem]">
              <label htmlFor="nota" className="block text-xs font-medium text-muted-foreground mb-1">
                Observação <span className="font-normal">(opcional)</span>
              </label>
              <input id="nota" className={inputCls} value={nota} maxLength={500}
                placeholder="ex.: sem o troco da entrega" onChange={e => setNota(e.target.value)} />
            </div>
            <button
              type="button" onClick={salvar} disabled={valorContado == null || salvando}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {salvando ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSave className="h-4 w-4" />}
              Gravar conferência
            </button>
          </div>

          {erro && <p className="mt-3 text-sm text-destructive">{erro}</p>}
          {ok && (
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
              <FiCheck className="h-4 w-4" />{ok}
            </p>
          )}
        </div>
        </PlnBloco>

        {/* ── Histórico ── */}
        <PlnBloco titulo="Conferências anteriores"
          nota={historico.length ? `${historico.length} ${historico.length === 1 ? 'conferência' : 'conferências'} deste caixa` : undefined}
          className="overflow-hidden">
          {historico.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              Nenhuma conferência gravada ainda para este caixa.
            </p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground">
                    <th className={`${stickyTh} text-left font-medium px-4 py-2`}>Data</th>
                    <th className={`${stickyTh} text-right font-medium px-4 py-2`}>Contado</th>
                    <th className={`${stickyTh} text-right font-medium px-4 py-2`}>Sistema</th>
                    <th className={`${stickyTh} text-right font-medium px-4 py-2`}>Diferença</th>
                    <th className={`${stickyTh} text-left font-medium px-4 py-2`}>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map(h => {
                    const d = Number(h.diff)
                    const zero = Math.abs(d) < 0.005
                    return (
                      <tr key={h.id} className="border-t border-border/60">
                        <td className="px-4 py-2 tabular-nums text-foreground">{ptDate(h.count_date)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-foreground">{brl(h.counted_amount)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{brl(h.system_amount)}</td>
                        <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                          zero ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                          {brl(d)}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{h.note || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PlnBloco>
      </div>
    </PanelShell>
  )
}
